import { Minus, Plus } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/app/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/app/components/ui/dialog";
import { Input } from "@/app/components/ui/input";
import type { ResumeContextValue } from "@/app/types/resume";
import AutoSuggestion from "../../../../components/AutoSuggestion";
import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems from "../../../../components/ResumeBuilder/EditorListItems";
import type { EditorListItem } from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SkillsEditorListItems from "../../../../components/ResumeBuilder/SkillsEditorListItems";
import SkipSectionDialog from "../../../../components/ResumeBuilder/SkipSectionDialog";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import {
	convertArrayToUl,
	convertDateToWords,
	convertSplittedSkillsToNormalSkillsArray,
	convertUlToArray,
	dateToMYObject,
	getFirstWordSeparedByFullColon,
	type ListItem,
	stripSkillGrouping,
} from "./../../../../handlers/utilityFunctions";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { workExperienceSchema } from "../../../../schemas/resume";
import { StaticValue } from "../../../../seed/constants";
import { getJobFunctionFromTitle } from "../../../../seed/getMetadata";
import suggestionsData from "../../../../seed/metadata/filemapping";
import { skillsForJobFunction } from "../../../../seed/skills/skillsMapping";
import Titles from "../../../../seed/titles";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import GroupedSkillsItem from "../../Skills/GroupedSkillsItem";

/** The work entry handed over from the work-history form. */
export interface WorkHistoryFormObject {
	referenceId: string;
	jobTitle: string;
	employer: string;
	city: string;
	region: string;
	country: string;
	startDate: string;
	endDate: string;
	currentEmployer: boolean | "";
	relevantSkills: string;
	details: string;
}

interface ResumeWorkDetailsProps {
	closeModal: () => void;
	workHistoryObject: WorkHistoryFormObject;
}

interface GroupedSkill {
	title: string;
	skills: { added: boolean; skill: string }[];
	collapsed: boolean;
}

const ResumeWorkDetails = ({ closeModal, workHistoryObject }: ResumeWorkDetailsProps) => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const patchSection = usePatchResumeSection();

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();

	function previous() {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete);
		navigate(-1);
	}

	const [workDetailList, setWorkDetailList] = useState<EditorListItem[]>([]);

	const [userInput, setUserInput] = useState(workHistoryObject.jobTitle);

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const { journal } = resumeData;

	const resumeCurrentStatus = journal ? journal.currentStatus : false;
	const isComplete = resumeCurrentStatus == "COMPLETED";

	const [workDetails, setWorkDetails] = useState("");
	useEffect(() => {
		if (resumeData.workHistory.length > 0 && workHistoryObject.referenceId) {
			setWorkDetails(() => {
				const data = resumeData.workHistory.filter((item) => item.referenceId === workHistoryObject.referenceId)[0];

				return data.details;
			});
		}
	}, [resumeData]);

	let [isInputCompleted, setIsInputCompleted] = useState(false);
	const [showSkipDialog, setShowSkipDialog] = useState(false);

	const resumeId = cookies[staticValues.localStorageResumeId];

	function onSuccess(ledger: unknown) {
		resumeObjectToContextFormat(ledger, setResumeData);
		skipSection();
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as { response: { data: { message: string | string[] } } }).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...(serverResponse.message as string[])];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	function addSuggestionsToList() {
		const data = getJobFunctionFromTitle(userInput).toString().toLowerCase();

		const jobFunction = data === "" ? resumeData.contactInfo.resumeFunction.toString().toLowerCase() : data;

		// Whole value first: splitting on the comma turns "Advertising, Arts and
		// Media" into "advertising" and matches nothing.
		const suggestions = suggestionsData[jobFunction] || suggestionsData[jobFunction.split(",")[0].trim()] || [];
		setWorkDetailList([]);

		if (suggestions && suggestions.length > 0) {
			let list: string[][] = [];
			[...suggestions].map((suggestion) => {
				const data = [...suggestion.responsibilities];
				return list.push(data);
			});
			//Remove duplicates
			const flatList: string[] = [...new Set(list.flat())];

			flatList.map((item, index) => {
				if (item !== "") {
					const value = {
						id: index,
						title: item,
						added: false,
					};

					setWorkDetailList((prev) => {
						return [...prev, value];
					});
				}
				return setWorkDetailList((prev) => {
					//remove empty values
					return [...prev].filter((n) => n.title.length !== 0);
				});
			});
		}
	}

	useEffect(() => {
		if (userInput.length > 0) {
			addSuggestionsToList();
		}
	}, [userInput]);

	const [relevantSkills, setRelevantSkills] = useState(workHistoryObject.relevantSkills || "");

	const addWorkDetails = () => {
		const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(groupedSkills);
		const customSkillsData = convertUlToArray(customSkills);
		const skillsData = formattedSkillsData.concat(customSkillsData);
		const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
		const ulSkillsData = formattedUlSkillsData.concat(customSkills);
		const updatedWorkDetails = {
			...workHistoryObject,
			details: workDetails,
			relevantSkills: ulSkillsData,
		};

		const validation = workExperienceSchema.safeParse({
			...updatedWorkDetails,
			address: `${updatedWorkDetails.city}, ${updatedWorkDetails.region}, ${updatedWorkDetails.country}`,
		});
		const hasWorkDetails = updatedWorkDetails.details !== "" && updatedWorkDetails.details !== "<p><br></p>";
		isInputCompleted = validation.success && hasWorkDetails;
		if (!validation.success || !hasWorkDetails) {
			setServerErrors([
				...(validation.success ? [] : validation.error.issues.map((issue) => issue.message)),
				...(hasWorkDetails ? [] : ["Please enter work details"]),
			]);
			return;
		}

		if (validation.success) {
			const responsibilitiesAndAccomplishments = convertUlToArray(updatedWorkDetails.details);

			const data: Record<string, unknown> = {
				referenceId: updatedWorkDetails.referenceId,
				jobtitle: updatedWorkDetails.jobTitle,
				employer: updatedWorkDetails.employer,
				employerLocation: {
					city: updatedWorkDetails.city,
					region: updatedWorkDetails.region,
					country: updatedWorkDetails.country,
				},
				startDate: dateToMYObject(updatedWorkDetails.startDate),
				endDate: updatedWorkDetails.endDate === "" ? null : dateToMYObject(updatedWorkDetails.endDate),
				currentEmployer: updatedWorkDetails.endDate === "" ? true : false,
				reponsibilitiesAndAccomplishements: responsibilitiesAndAccomplishments,
				// Drop blanks. The custom-skills editor contributes an empty paragraph
				// when it has never been typed in, which was stored as a `{value: ""}`
				// entry against the job. The Skills screen already filters these out.
				relevantSkills: skillsData.filter((skill) => skill.value !== ""),
			};

			patchSection.mutate({ resumeId, section: RESUME_SECTIONS.EXPERIENCE, data } as never, { onSuccess, onError });
		}
	};

	const [skillList, setSkillList] = useState<EditorListItem[]>([]);
	const [showRelevantSkillsInput, setShowRelevantSkillsInput] = useState(workHistoryObject.relevantSkills.length > 10);

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
		addSuggestionsSkillsSuggestionsToList();
	}, [resumeData]);

	function addSuggestionsSkillsSuggestionsToList() {
		const jobFunction = resumeData.contactInfo.resumeFunction.toString().toLowerCase();

		const suggestions: string[] = skillsForJobFunction(jobFunction);
		setSkillList([]);

		if (suggestions && suggestions.length > 0) {
			const list = [...suggestions];

			list.map((item, index) => {
				if (item !== "") {
					const value = {
						id: index,
						title: item,
						added: false,
					};
					setSkillList((prev) => {
						return [...prev, value];
					});
				}
				return setSkillList((prev) => {
					//remove empty values
					return [...prev].filter((n) => n.title.length !== 0);
				});
			});
		}
	}
	useEffect(() => {
		addSuggestionsSkillsSuggestionsToList();
	}, [workHistoryObject.jobTitle]);

	const [filteredSkillList, setFilteredSkillList] = useState<EditorListItem[]>([]);

	useEffect(() => {
		setFilteredSkillList(skillList);
	}, [skillList]);

	useEffect(() => {
		setIsInputCompleted(workDetails !== "" && workDetails !== "<p><br></p>");
	}, [workDetails]);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link);
		return navigate("/resume/work-history-summary");
	};

	const [addCustomCategory, setAddCustomCategory] = useState(false);
	const [groupedSkills, setGroupedSkills] = useState<GroupedSkill[]>([]);
	const [customSkills, setCustomSkills] = useState("");

	function getGroupedAndCustomSkills(data: string) {
		// NOTE: entries intentionally have no `bp` flag — convertArrayToUl then
		// serialises them as `<p>...</p>`, exactly like the legacy page did.
		const newCustomSkills = [] as ListItem[];
		const skillsInGroup = convertUlToArray(data)
			.map((skillObject) => {
				const splitArray = skillObject.value.split(/:(.+)/);
				const wordBeforeColon = splitArray[0].trim();
				if (splitArray.length > 1) {
					let groupedSkills: { added: boolean; skill: string }[] = [];
					groupedSkills = groupedSkills.concat(
						splitArray[1]
							.trim()
							.split(",")
							.map((skillItem: string) => {
								// See the matching parse in ResumeSkils: splitting on "," leaves a
								// leading space that the rebuild's ", " join then doubles, and the
								// source data's grouping braces are not for the reader.
								return { added: true, skill: stripSkillGrouping(skillItem) };
							}),
					);
					return { title: wordBeforeColon, skills: groupedSkills, collapsed: true };
				}
				newCustomSkills.push({ value: wordBeforeColon } as ListItem);
			})
			.filter((item): item is GroupedSkill => item !== undefined);
		setGroupedSkills(skillsInGroup);
		setCustomSkills(convertArrayToUl(newCustomSkills));
		newCustomSkills.length > 0 && setAddCustomCategory(true);
	}

	const collapseGroupedSkills = (groupedSkillIndex: number) => {
		const newGroupedSkills = [
			...groupedSkills.map((skillGroup) => ({
				...skillGroup,
				collapsed: skillGroup === groupedSkills[groupedSkillIndex] ? !skillGroup.collapsed : skillGroup.collapsed,
			})),
		];
		setGroupedSkills(newGroupedSkills);
	};

	const removeGroupedSkills = (skillTitle: string) => {
		let newGroupedSkills = groupedSkills;
		newGroupedSkills = newGroupedSkills.filter((skillItem) => skillItem.title !== skillTitle);
		setGroupedSkills(newGroupedSkills);

		const indexChanged = filteredSkillList.findIndex(
			(changed) => getFirstWordSeparedByFullColon(changed.title) === skillTitle,
		);
		if (indexChanged !== -1) {
			// Immutable update: new array + new object for the changed item, so
			// current state is not mutated and React sees a fresh reference.
			setFilteredSkillList((prev) =>
				prev.map((item, index) => (index === indexChanged ? { ...item, added: false } : item)),
			);
		}
	};

	const onSelectSingleSkill = (groupedSkillIndex: number, singleSkillIndex: number) => {
		const newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills[singleSkillIndex].added =
			!newGroupedSkills[groupedSkillIndex].skills[singleSkillIndex].added;

		setGroupedSkills(newGroupedSkills);
	};

	const onAddCustomSkill = (groupedSkillIndex: number) => {
		const newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills = [
			...newGroupedSkills[groupedSkillIndex].skills,
			{ added: true, skill: "" },
		];

		setGroupedSkills(newGroupedSkills);
	};

	const onSaveCustomSkillAdded = (groupedSkillIndex: number, singleSkillIndex: number, skillAdded: string) => {
		const newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills[singleSkillIndex].skill = skillAdded;

		setGroupedSkills(newGroupedSkills);
	};

	const toggleEditorVisibility = () => {
		const newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
			collapsed: !addCustomCategory,
		}));
		setGroupedSkills(newGroupedSkills);
		setAddCustomCategory(!addCustomCategory);
	};

	useEffect(() => {
		getGroupedAndCustomSkills(workHistoryObject.relevantSkills || "");
	}, []);

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<Dialog
				open
				onOpenChange={(open) => {
					if (!open) closeModal();
				}}
			>
				<DialogContent aria-describedby={undefined} className="block max-h-[80vh] overflow-y-auto sm:max-w-[80vw]">
					<div className="mb-2 flex items-center justify-between pr-8">
						<DialogTitle className="font-semibold text-base text-gray-700">
							Use 3-5 bullet points to highlight your key achievements and responsibilities.
						</DialogTitle>
					</div>
					{serverErrors.map((item, index) => {
						return <ErrorToast error={item} key={index} />;
					})}
					<div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
						<div className="mb-4">
							<div>
								<p className="mb-0 text-gray-900">
									<span>{workHistoryObject.jobTitle}</span>, <span>{workHistoryObject.employer}</span>
								</p>
								<p className="mb-3 text-gray-900">
									{workHistoryObject.city}, {workHistoryObject.region}, {workHistoryObject.country} •{" "}
									{convertDateToWords(workHistoryObject.startDate)} -{" "}
									{workHistoryObject.endDate ? convertDateToWords(workHistoryObject.endDate) : "Present"}
								</p>
							</div>
							<div className="mb-4">
								<AutoSuggestion suggestions={Titles} userInput={userInput} setUserInput={setUserInput} isForm={false} />
							</div>
							{/* recommendations topics  */}
							<div className="max-h-[45vh] overflow-y-auto">
								<EditorListItems
									editorListItems={workDetailList}
									editorContent={workDetails}
									setEditorContent={setWorkDetails}
									setEditorListItems={setWorkDetailList}
								/>
							</div>
						</div>
						<div className="mb-4 flex flex-col justify-end">
							<div>
								<TextEditor
									id="workDetails"
									value={workDetails}
									setValue={setWorkDetails}
									placeholder="Type your achievements and responsibilities here."
								/>
							</div>
						</div>
					</div>
					<div className="flex gap-1 items-center">
						{showRelevantSkillsInput ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
						<p className="text-sm" onClick={() => setShowRelevantSkillsInput(!showRelevantSkillsInput)}>
							Highlight relevant skills you applied as a{" "}
							<span className="font-semibold text-primary-700">{workHistoryObject.jobTitle}</span> at{" "}
							<span className="font-semibold text-primary-700">{workHistoryObject.employer}</span>
						</p>
					</div>
					{showRelevantSkillsInput && (
						<div className="p-3">
							<div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
								<div className="mb-4">
									<div className="mb-3 flex">
										<Input
											type="text"
											onChange={(e) => {
												const input = e.target.value;
												if (input.trim() !== "") {
													setFilteredSkillList((prev) =>
														prev.filter((skill) => skill.title.toLowerCase().includes(input.toLowerCase())),
													);
												} else {
													setFilteredSkillList(skillList);
												}
											}}
											placeholder="Search"
											className="rounded-r-none"
										/>
										<Button type="button" className="rounded-l-none md:px-10">
											<span className="fa fa-search" />
										</Button>
									</div>
									<div className="max-h-[45vh] overflow-y-auto">
										<SkillsEditorListItems
											editorListItems={filteredSkillList}
											editorContent={relevantSkills}
											setEditorContent={setRelevantSkills}
											setEditorListItems={setFilteredSkillList}
											groupedSkills={groupedSkills}
											setGroupedSkills={setGroupedSkills}
										/>
									</div>
								</div>
								<div className="mb-4 flex flex-col justify-end">
									{groupedSkills.map((item, groupedSkillIndex) => {
										if (item.title === "") {
											return null;
										}

										return (
											<GroupedSkillsItem
												key={groupedSkillIndex}
												item={item}
												collapseGroupedSkills={collapseGroupedSkills}
												groupedSkillIndex={groupedSkillIndex}
												removeGroupedSkills={removeGroupedSkills}
												onSaveCustomSkillAdded={onSaveCustomSkillAdded}
												onSelectSingleSkill={onSelectSingleSkill}
												onAddCustomSkill={onAddCustomSkill}
											/>
										);
									})}

									{addCustomCategory && (
										<div>
											<TextEditor
												value={customSkills}
												setValue={setCustomSkills}
												placeholder="Add custom skills..."
												toggleEditorVisibility={toggleEditorVisibility}
											/>
										</div>
									)}
									{!addCustomCategory && (
										<button type="button" className="cursor-pointer text-left" onClick={toggleEditorVisibility}>
											<span className="fa fa-plus mr-2" />
											<span className="font-medium text-sm">Add custom category</span>
										</button>
									)}
								</div>
							</div>
						</div>
					)}
					<div className="mt-4 flex items-center justify-end gap-2">
						<button
							type="button"
							onClick={() => previous()}
							className="cursor-pointer px-4 py-2 font-semibold text-primary-700"
						>
							Cancel
						</button>
						<Button
							type="button"
							onClick={() => {
								if (!isInputCompleted) {
									setShowSkipDialog(true);
								}
								addWorkDetails();
							}}
						>
							{patchSection.isPending ? (
								<>
									Loading <Spinner />
								</>
							) : (
								"Save and Continue"
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ResumeWorkDetails;
